import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { getAdminStats } from '../../api/client';
import {
  LayoutDashboard,
  BookOpen,
  Users,
  CreditCard,
  Mail,
  Settings,
  GraduationCap,
  ShieldAlert,
  LifeBuoy,
  DollarSign,
  FileText,
  ScrollText,
  ArrowRight,
  Sparkles,
  Zap,
  TrendingUp,
  Clock,
} from 'lucide-react';

interface StatsData {
  total_users: number;
  total_courses: number;
  total_enrollments: number;
  total_revenue: number;
  total_leads: number;
  pending_reviews: number;
  pending_tickets: number;
  pending_flags: number;
  pending_payouts: number;
}

interface RecentUser {
  id: number;
  name: string;
  email: string;
  role: string;
  created_at: string;
}

interface RecentPayment {
  id: number;
  amount: number;
  status: string;
  created_at: string;
  name: string;
  email: string;
  course_title: string;
}

interface AdminStatsResponse {
  stats: StatsData;
  recent_users: RecentUser[];
  recent_payments: RecentPayment[];
  recent_leads: RecentLead[];
}

interface RecentLead {
  id: number;
  name: string;
  email: string;
  phone: string;
  source: string;
  created_at: string;
}

interface SectionCard {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  color: string;
  bgGradient: string;
  borderColor: string;
  textColor: string;
  statKey?: keyof StatsData;
  statLabel: string;
  badge?: string;
}

const sections: SectionCard[] = [
  {
    id: 'academy',
    title: 'Academy',
    description: 'Manage courses, curriculum, and student enrollments',
    icon: BookOpen,
    path: '/admin/courses',
    color: 'purple',
    bgGradient: 'from-purple-500 to-violet-600',
    borderColor: 'border-purple-200',
    textColor: 'text-purple-600',
    statKey: 'total_courses',
    statLabel: 'Courses',
  },
  {
    id: 'community',
    title: 'Community',
    description: 'Manage users, roles, and content moderation',
    icon: Users,
    path: '/admin/users',
    color: 'blue',
    bgGradient: 'from-blue-500 to-indigo-600',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-600',
    statKey: 'total_users',
    statLabel: 'Users',
    badge: 'moderation',
  },
  {
    id: 'finance',
    title: 'Finance',
    description: 'Track payments, revenue, and trainer payouts',
    icon: CreditCard,
    path: '/admin/payments',
    color: 'emerald',
    bgGradient: 'from-emerald-500 to-teal-600',
    borderColor: 'border-emerald-200',
    textColor: 'text-emerald-600',
    statKey: 'total_revenue',
    statLabel: 'Revenue',
  },
  {
    id: 'marketing',
    title: 'Marketing',
    description: 'Manage leads, campaigns, and CMS content',
    icon: Mail,
    path: '/admin/marketing',
    color: 'rose',
    bgGradient: 'from-rose-500 to-pink-600',
    borderColor: 'border-rose-200',
    textColor: 'text-rose-600',
    statKey: 'total_leads',
    statLabel: 'Leads',
  },
  {
    id: 'operations',
    title: 'Operations',
    description: 'Handle support tickets and audit logs',
    icon: LifeBuoy,
    path: '/admin/support',
    color: 'cyan',
    bgGradient: 'from-cyan-500 to-sky-600',
    borderColor: 'border-cyan-200',
    textColor: 'text-cyan-600',
    statKey: 'pending_tickets',
    statLabel: 'Open Tickets',
    badge: 'tickets',
  },
  {
    id: 'system',
    title: 'System',
    description: 'Configure site settings and preferences',
    icon: Settings,
    path: '/admin/settings',
    color: 'slate',
    bgGradient: 'from-slate-500 to-gray-600',
    borderColor: 'border-slate-200',
    textColor: 'text-slate-600',
    statLabel: 'Settings',
  },
];

const quickStats = [
  { key: 'total_users' as const, label: 'Total Users', icon: Users, color: 'bg-blue-50 text-blue-600' },
  { key: 'total_courses' as const, label: 'Courses', icon: BookOpen, color: 'bg-purple-50 text-purple-600' },
  { key: 'total_enrollments' as const, label: 'Enrollments', icon: GraduationCap, color: 'bg-emerald-50 text-emerald-600' },
  { key: 'total_revenue' as const, label: 'Revenue', icon: DollarSign, color: 'bg-amber-50 text-amber-600' },
];

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' as const } },
};

export default function AdminDashboard(): React.JSX.Element {
  const toast = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<AdminStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchStats() {
      try {
        setLoading(true);
        setError(null);
        const res = await getAdminStats();
        if (!cancelled) {
          setData(res as AdminStatsResponse);
        }
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Failed to load dashboard data';
          setError(msg);
          toast.show?.(msg, 'error');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchStats();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const stats = data?.stats;

  const getBadgeCount = (section: SectionCard): number | null => {
    if (!stats) return null;
    if (section.badge === 'moderation') return stats.pending_flags;
    if (section.badge === 'tickets') return stats.pending_tickets;
    return null;
  };

  const getStatValue = (section: SectionCard): string => {
    if (!stats || !section.statKey) return '-';
    const value = stats[section.statKey];
    if (section.statKey === 'total_revenue') {
      return `₹${(Number(value) / 100000).toFixed(1)}L`;
    }
    return Number(value).toLocaleString();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-8 h-8 border-4 border-slate-200 border-t-amber-500 rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-96 gap-4">
          <p className="text-red-600 font-semibold">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-amber-500 text-white rounded-xl text-sm font-bold hover:bg-amber-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-8"
      >
        {/* Welcome Header */}
        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <h1 className="text-2xl font-bold text-slate-900">
                {getGreeting()}, {user?.name?.split(' ')[0] || 'Admin'}
              </h1>
            </div>
            <p className="text-sm text-slate-500">
              Welcome to your command center. Here&apos;s everything at a glance.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400 bg-white px-3 py-2 rounded-xl border border-slate-100 shadow-sm">
            <Clock className="w-3.5 h-3.5" />
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </div>
        </motion.div>

        {/* Quick Stats */}
        <motion.div variants={itemVariants}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {quickStats.map((stat) => {
              const Icon = stat.icon;
              const value = stats?.[stat.key] as number || 0;
              return (
                <div
                  key={stat.key}
                  className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-10 h-10 rounded-xl ${stat.color} flex items-center justify-center`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <TrendingUp className="w-4 h-4 text-green-500" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {stat.key === 'total_revenue' ? `₹${(value / 100000).toFixed(1)}L` : value.toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Section Cards Grid */}
        <motion.div variants={itemVariants}>
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-4 h-4 text-amber-500" />
            <h2 className="text-lg font-bold text-slate-900">Management Modules</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {sections.map((section, index) => {
              const Icon = section.icon;
              const badgeCount = getBadgeCount(section);
              return (
                <motion.div
                  key={section.id}
                  variants={itemVariants}
                  whileHover={{ y: -4, scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => navigate(section.path)}
                  className={`
                    group relative bg-white rounded-2xl border ${section.borderColor}
                    shadow-sm hover:shadow-lg cursor-pointer
                    overflow-hidden transition-all duration-300
                  `}
                >
                  {/* Top gradient accent bar */}
                  <div className={`h-1.5 w-full bg-gradient-to-r ${section.bgGradient}`} />

                  <div className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${section.bgGradient} flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex items-center gap-2">
                        {badgeCount !== null && badgeCount > 0 && (
                          <span className="px-2 py-1 rounded-full bg-red-50 text-red-600 text-[10px] font-bold">
                            {badgeCount} pending
                          </span>
                        )}
                        <div className={`w-8 h-8 rounded-xl ${section.textColor.replace('text-', 'bg-').replace('600', '50')} flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity`}>
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>

                    <h3 className="text-base font-bold text-slate-900 mb-1">{section.title}</h3>
                    <p className="text-sm text-slate-500 mb-4 line-clamp-2">{section.description}</p>

                    <div className="flex items-center justify-between pt-3 border-t border-slate-50">
                      <div>
                        <p className="text-xl font-bold text-slate-900">{getStatValue(section)}</p>
                        <p className="text-[11px] text-slate-400 font-medium uppercase tracking-wider">{section.statLabel}</p>
                      </div>
                      <div className={`text-xs font-semibold ${section.textColor} opacity-0 group-hover:opacity-100 transition-opacity`}>
                        Open →
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Recent Activity Row */}
        <motion.div variants={itemVariants} className="grid lg:grid-cols-2 gap-6">
          {/* Recent Users */}
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-slate-900">Recent Users</h3>
                <p className="text-xs text-slate-400">New registrations this week</p>
              </div>
              <Link to="/admin/users" className="text-sm text-amber-600 font-semibold hover:underline">
                View All
              </Link>
            </div>
            <div className="divide-y divide-slate-50">
              {data?.recent_users.slice(0, 4).map((user) => (
                <div key={user.id} className="p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors">
                  <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-bold text-sm">
                    {user.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{user.name}</p>
                    <p className="text-xs text-slate-400">{user.email}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    user.role === 'trainer' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-600'
                  }`}>
                    {user.role}
                  </span>
                  <span className="text-xs text-slate-400">{formatRelativeTime(user.created_at)}</span>
                </div>
              ))}
              {!data?.recent_users?.length && (
                <div className="p-8 text-center text-sm text-slate-400">No recent users</div>
              )}
            </div>
          </div>

          {/* Recent Payments */}
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-slate-900">Recent Payments</h3>
                <p className="text-xs text-slate-400">Latest transactions</p>
              </div>
              <Link to="/admin/payments" className="text-sm text-amber-600 font-semibold hover:underline">
                View All
              </Link>
            </div>
            <div className="divide-y divide-slate-50">
              {data?.recent_payments.slice(0, 4).map((payment) => (
                <div key={payment.id} className="p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                    payment.status === 'success' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'
                  }`}>
                    <DollarSign className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{payment.name}</p>
                    <p className="text-xs text-slate-400 truncate">{payment.course_title}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-slate-900">₹{payment.amount.toLocaleString()}</p>
                    <span className={`text-[10px] font-bold ${
                      payment.status === 'success' ? 'text-emerald-600' : 'text-amber-600'
                    }`}>
                      {payment.status}
                    </span>
                  </div>
                </div>
              ))}
              {!data?.recent_payments?.length && (
                <div className="p-8 text-center text-sm text-slate-400">No recent payments</div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Action Banner */}
        <motion.div variants={itemVariants}>
          <div
            onClick={() => navigate('/admin/courses')}
            className="relative bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 cursor-pointer hover:shadow-lg transition-shadow overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/4" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/4" />
            <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-white font-bold text-lg">Ready to add new content?</h3>
                <p className="text-white/80 text-sm">Create a new course and start engaging your students today.</p>
              </div>
              <div className="flex items-center gap-2 text-white font-semibold text-sm bg-white/20 px-4 py-2.5 rounded-xl hover:bg-white/30 transition-colors w-fit">
                <BookOpen className="w-4 h-4" />
                Create Course
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AdminLayout>
  );
}
